'use client';

import React, { useMemo, useEffect } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faClipboardList } from '@fortawesome/pro-regular-svg-icons';
import { useGetEngageStrategyDetailsQuery } from 'src/api/redux/strategy/engageStrategyApi';
import { useGetChainedStrategyDetailsQuery } from 'src/api/redux/strategy/chainedStrategiesApi';
import useProfileStrategies from 'src/hooks/strategy/useProfileStrategies';
import { useParams } from 'next/navigation';
import {
  useScoutContext,
  toScoutSelection,
  SCOUT_CONTEXT_KEYS,
} from 'src/hooks/scout';
import { PulseButton } from 'src/components/PulseUI';
import { useEngageStrategyConnector } from 'src/components/Profiles/shared/widgets/hooks/useEngageStrategyConnector';
import { StrategyErrorState } from './components/StrategyErrorState';
import { StrategyLoadingState } from './components/StrategyLoadingState';
import { extractErrorMessage } from './utils/errorUtils';
import { STRATEGY_VIEWS } from './index';
import { ReferralsView } from './Views';

const StrategyProfileView: React.FC = () => {
  const { id, strategyId } = useParams<{ id: string; strategyId: string }>();

  const { resetProfileStrategy, selectedProviders } = useProfileStrategies();

  // Share the selected providers (IDs and count only) with Scout, from the
  // list or the map.
  const scoutSelection = useMemo(
    () =>
      toScoutSelection(
        (selectedProviders ?? []).map((provider) => provider?.provider_id)
      ),
    [selectedProviders]
  );
  useScoutContext(
    SCOUT_CONTEXT_KEYS.PROFILE_STRATEGY_SELECTION,
    'Selected strategy providers',
    scoutSelection
  );

  // Reset Redux state when strategyId changes
  useEffect(() => {
    resetProfileStrategy();
  }, [strategyId, resetProfileStrategy]);

  // Fetch chain details to get the engage strategy ID
  const {
    data: chainData,
    isLoading: isChainLoading,
    error: chainError,
  } = useGetChainedStrategyDetailsQuery(strategyId, {
    skip: !strategyId,
  });

  // Extract engage strategy ID from chain (use first one)
  const engageStrategyId = useMemo(() => {
    if (!chainData?.engage_strategies?.length) return null;
    return chainData?.engage_strategies?.[0]?.id?.toString() || null;
  }, [chainData]);

  // Check connector status before fetching engage data
  const {
    connectorData,
    isConnectorLoading,
    isConnector404,
    isProcessing,
    isExecuting,
    handleExecuteStrategy,
  } = useEngageStrategyConnector({
    strategyId: engageStrategyId ?? '',
    providerId: id ?? '',
  });

  // Fetch engage strategy details to get type
  const { data: engageStrategyDetails } = useGetEngageStrategyDetailsQuery(
    engageStrategyId || '',
    {
      skip: !engageStrategyId,
    }
  );

  // Show loading state while fetching chain or connector
  if (isChainLoading || isConnectorLoading) {
    return <StrategyLoadingState />;
  }

  // Show error state if chain fetch failed
  if (chainError) {
    const errorMessage = extractErrorMessage(
      chainError,
      'Unable to load strategy details. If this issue persists, please contact your Customer Success team member for assistance.'
    );

    return (
      <StrategyErrorState
        title="Error Loading Strategy Chain"
        message={errorMessage}
      />
    );
  }

  // If no engage strategy, don't show map/results - parent will only show metrics
  if (!engageStrategyId) {
    return null;
  }

  // Show not-run state when connector doesn't exist for this provider
  if (isConnector404) {
    return (
      <div
        className="flex flex-col items-center justify-center p-8 text-center"
        role="status"
        data-testid="strategy-profile-not-run"
      >
        <div
          className="w-20 h-20 bg-gradient-to-br from-neutral-50 to-neutral-100 rounded-2xl flex items-center justify-center mb-6 shadow-sm"
          aria-hidden="true"
        >
          <Icon icon={faClipboardList} className="text-neutral-400" size="xl" />
        </div>
        <p className="text-sm text-neutral-500 mb-4">
          This strategy has not been run for this provider yet.
        </p>
        <PulseButton
          variant="solid"
          color="primary"
          onClick={handleExecuteStrategy}
          loading={isExecuting}
          aria-label="Execute engage strategy"
          data-testid="strategy-profile-execute-button"
        >
          Execute Engage Strategy
        </PulseButton>
      </div>
    );
  }

  // Show processing state when connector is executing
  if (isProcessing) {
    const processingMessage =
      connectorData?.status === 'CREATED'
        ? 'Strategy queued...'
        : 'Running strategy...';

    return (
      <div
        className="flex flex-col items-center justify-center p-8 gap-4"
        role="status"
        aria-label="Strategy is processing"
        data-testid="strategy-profile-processing"
      >
        <div className="flex gap-1.5">
          <span className="w-2 h-2 rounded-full bg-sky-500 animate-bounce [animation-delay:0ms]" />
          <span className="w-2 h-2 rounded-full bg-sky-500 animate-bounce [animation-delay:150ms]" />
          <span className="w-2 h-2 rounded-full bg-sky-500 animate-bounce [animation-delay:300ms]" />
        </div>
        <p className="text-sm font-medium text-neutral-500">
          {processingMessage}
        </p>
      </div>
    );
  }

  // Get the view component from registry based on type, fallback to ReferralsView
  const ViewComponent =
    STRATEGY_VIEWS[engageStrategyDetails?.type || ''] ?? ReferralsView;

  return <ViewComponent engageStrategyId={engageStrategyId} />;
};

export default StrategyProfileView;
